import React from 'react';
import find from 'lodash/find';
import startCase from 'lodash/startCase';
import moment from 'moment';
import {
  followUpTypeIDs,
  customersContactsPropertiesInfo,
  keysNotAllowedUpdated,
  poolKeysDateValue,
  poolPropertiesInfo,
} from './timelineConstants';
import {fillStars, systemOptionsTags} from 'helpers/dataHelpers';
import {
  systemTypeNamesFormat,
  chemicalBaseTypeNames,
  systemTypeFullNamesFormat,
  truBlueStatusFormat,
} from './constants';
import {TimelineType, TimelineAction} from '../../models/enums/timeline';
import {formatCurrency} from './dashboardFormatter.jsx';
import {FormattedMessage} from 'react-intl';
import {newNotesEnabled} from 'features';


// Outputs keys and values of a timeline changes object in a JSX array for customer/contact
function formatAccountContactUpdated(changes, formatMessage) {
  const output = [];

  if (!changes) {
    return output;
  }

  changes.map((d, i) => {
    if (keysNotAllowedUpdated.includes(d.Type)) {
      return;
    }

    let properties = customersContactsPropertiesInfo(formatMessage);
    let propertyInfo = properties[d.Type];

    if(propertyInfo) {
      let label = propertyInfo.label;

      if(d.Type === 'ContactEmailPreferences') {
        output.push(
          <div key={i}>
            <FormattedMessage id='timeline.field.changed'
                              values={{field: label}}/>
          </div>
        );
      }else{
        output.push(
          <div key={i}>
            {!d.New.toString() ?
              <FormattedMessage id='timeline.field.removed'
                            values={{field: label, old: d.Old.toString()}}/> :
          d.Old.toString() || d.Old.toString() !== '' ?
            <FormattedMessage id='timeline.field.changed.value'
                              values={{field: label, old: d.Old.toString(), new: d.New.toString()}}/> :
            <FormattedMessage id='timeline.field.added'
                              values={{field: label, new: d.New.toString()}}/>}
          </div>
        );
      }
    }
  });
  return output;
}

function splitOnCapital(string) {
  return string.split(/(?=[A-Z])/).join(' ');
}

//outputs keys and values of a timeline changes object in a JSX array for system
function formatSystemObject(systemType, changes, formatMessage, branch, chemicalBaseTypeID) {
  if (!changes) {
    return [];
  }

  return changes.map((d, i) => {


    let poolProperties = poolPropertiesInfo(formatMessage, branch, systemType, chemicalBaseTypeID);
    let propertyInfo = poolProperties[d.Type];

    if(propertyInfo) {
      let label = propertyInfo.label;
      let formattedNew = !propertyInfo.isProd ? d.New : null;
      let formattedOld = !propertyInfo.isProd ? d.Old : null;

      if (propertyInfo.valuesNeedTranslation) {
        if (d.Type === 'ChemicalBaseTypeID') {
          formattedOld = d.Old ? chemicalBaseTypeNames(systemType, d.Old, formatMessage) : null;
          formattedNew = d.New ? chemicalBaseTypeNames(systemType, d.New, formatMessage) : null;
        } else if (d.Type === 'GroundLevel') {
          let oldTag = systemOptionsTags[systemType].groundLevel[d.Old];
          let newTag = systemOptionsTags[systemType].groundLevel[d.New];

          formattedOld = d.Old && oldTag ? formatMessage({id: oldTag}) : null;
          formattedNew = d.New && newTag ? formatMessage({id: newTag}) : null;
        } else if (d.Type === 'FilterMedia') {
          let oldTag = systemOptionsTags[systemType].filterMedia[d.Old];
          let newTag = systemOptionsTags[systemType].filterMedia[d.New];

          formattedOld = d.Old && oldTag ? formatMessage({id: oldTag}) : null;
          formattedNew = d.New && newTag ? formatMessage({id: newTag}) : null;
        } else if (d.Type === 'TruBlueStatus') {
          formattedOld = d.Old ? truBlueStatusFormat(formatMessage)[startCase(d.Old.toLowerCase())] : null;
          formattedNew = d.New ? truBlueStatusFormat(formatMessage)[startCase(d.New.toLowerCase())] : null;
        } else if(d.Type === 'TypeID') {
          formattedOld = d.Old ? systemTypeNamesFormat(formatMessage)[d.Old] : null;
          formattedNew = d.New ? systemTypeNamesFormat(formatMessage)[d.New] : null;
        }else {
          let oldTag = systemOptionsTags[systemType][d.Type.toLowerCase()][d.Old];
          let newTag = systemOptionsTags[systemType][d.Type.toLowerCase()][d.New];

          formattedOld = d.Old && oldTag ? formatMessage({id: oldTag}) : null;
          formattedNew = d.New && newTag ? formatMessage({id: newTag}) : null;
        }
      }

      if(propertyInfo.isDate) {
        formattedOld = moment(d.Old).isValid() ? moment(d.Old).format('ll') : d.Old;
        formattedNew = moment(d.New).isValid() ? moment(d.New).format('ll') : d.New;
      }

      if (!formattedOld && formattedNew) {
        return <div key={i}><FormattedMessage id='timeline.field.added'
                                            values={{field: label}}/></div>;
      }

      if (formattedOld && !formattedNew) {
        return <div key={i}><FormattedMessage id='timeline.field.removed'
                                            values={{field: label}}/></div>;
      }

      if (!formattedOld && !formattedNew) {
        return <div key={i}>
          <FormattedMessage id='timeline.field.changed'
                                            values={{field: label}}/>
        </div>;
      }

      return <div key={i}>
        <FormattedMessage id='timeline.field.changed.value'
                        values={{field: label, old: formattedOld, new: formattedNew}}/>
      </div>;
    }
    return null;
  });
}

//get simple output of an array and output jsx
function getArrayDataEmails(data, currentContacts) {
  if (!data) {
    return '';
  }

  return <React.Fragment>
    {data.map((d, i) => {
      let name = currentContacts.find(x => x.email === d);

      return <div key={i}>
        {!name ? d : <FormattedMessage id='timeline.email.sent.to' values={{name: name.name, email: d}} />}
      </div>;
    })}
  </React.Fragment>;
}

//account merge JSC generator
function accountMergeInfo(old) {
  if (typeof old === 'string') {
    old = JSON.parse(old);
  }

  if (!(old instanceof Array)) {
    return null;
  }

  return old
    .filter(d => d && d.CustomerContacts[0])
    .map((d, i) => <div key={i}>
      <div><FormattedMessage id='timeline.account.number'
                             values={{accountNumber: d.AccountNo}}/></div>
      <div>{d.CustomerContacts[0].NameFirst} {d.CustomerContacts[0].NameLast}</div>
    </div>);
}

function formatInvoice(lineitems) {
  try {
    if (!(lineitems && lineitems.length > 0)) {
      return <div><FormattedMessage id='timeline.products.none'/></div>;
    }

    let products = lineitems.map((l, i) => {
      if (l.IsBioguardProduct) {
        return <div key={i}>({l.Quantity}) {l.AmountInProduct + l.UOM + ' ' + l.Name}</div>;
      }

      return <div key={i}/>;
    });

    return <div>
      <strong> Products: </strong>
      {products}
    </div>;
  } catch (e) {
    return <div><FormattedMessage id='timeline.products.none'/></div>;
  }
}

//outplus highlight class if  input type macthes type of change object
function getHighlighted(changes, type) {
  let output = '';

  changes.map(d => {
    if (d.Type === type) {
      output = 'highlight-change';
    }
  });
  return output;
}

function getLabelID(id) {
  return !!id && id.length ? id : 'general.label.notFound';
}

export default function getEventDescription(event, branch, currentContacts, formatMessage) {
  let description;

  if (event && event.data && event.data.data) {
    const isHistoricalNote =  event.data.data.hasOwnProperty('Flag') || event.data.data.hasOwnProperty('flag');

    switch (event.type) {
      case TimelineType.FollowUpDismissed:
      case TimelineType.FollowUpAdded:
      case TimelineType.FollowUpDeleted:
      case TimelineType.FollowUpPhoneCall:
        description = <div>
          <div><FormattedMessage id='timeline.follow-up.date'
                                 values={{date: moment(event.Created).format('ll h:mm a')}}/></div>
          <div><FormattedMessage id='timeline.follow-up.type'
                                 values={{type: followUpTypeIDs(formatMessage)[event.data.data.FollowUpTypeID]}}/></div>
        </div>;
        break;
      case TimelineType.PoolOpened:
      case TimelineType.PoolClosed:
      case TimelineType.AnalysisImported:
      case TimelineType.SystemAnalysisAdded:
      case TimelineType.SystemAnalysisDeleted:
        if(event.action === TimelineAction.Imported) {break;}
        description = <div>
          <div><FormattedMessage id='timeline.poolScore.comfort'/> {fillStars(event.data.data.ComfortScore)}</div>
          <div><FormattedMessage id='timeline.poolScore.clarity'/> {fillStars(event.data.data.ClarityScore)}</div>
          <div><FormattedMessage id='timeline.poolScore.protection'/> {fillStars(event.data.data.ProtectionScore)}</div>
        </div>;
        break;
      case TimelineType.AccountNoteAdded:
      case TimelineType.AccountNoteDeleted:
      case TimelineType.AccountNoteUpdated:
        description = !newNotesEnabled || isHistoricalNote ? event.data.data.Title : <div>
          <div className='bold'>{event.data.data.title}</div>
          <div>{event.data.data.note}</div>
        </div>;
        break;
      case TimelineType.CustomerImported:
      case TimelineType.AccountAdded:
      case TimelineType.CustomerVerifyAddress:
        description = <div>
          {event.data.data.Address1 ? <div><FormattedMessage id='timeline.address'
                                                             values={{address: event.data.data.Address1}}/>
          </div> : null}
          {event.data.data.City ? <div><FormattedMessage id='timeline.address.city'
                                                         values={{city: event.data.data.City}}/></div> : null}
          {event.data.data.State ? <div><FormattedMessage id='timeline.address.state'
                                                          values={{state: event.data.data.State}}/></div> : null}
          {event.data.data.Zip ? <div><FormattedMessage id='timeline.address.zip'
                                                        values={{zip: event.data.data.Zip}}/></div> : null}
          {event.data.data.Country ? <div><FormattedMessage id='timeline.address.country'
                                                            values={{country: event.data.data.Country}}/></div> : null}
        </div>;
        break;
      case TimelineType.AccountUpdated:
        description = <div>{formatAccountContactUpdated(event.data.changes, formatMessage)}</div>;
        break;
      case TimelineType.AccountDeleted:
        description = <div>
          <FormattedMessage
            id='timeline.customer.deleted'
            values={{customer: event.data.data.CustomerContacts[0].NameFirst + ' ' + event.data.data.CustomerContacts[0].NameLast}}/>
        </div>;
        break;
      case TimelineType.SystemNoteAdded:
      case TimelineType.SystemNoteDeleted:
      case TimelineType.SystemNoteUpdated:
        description = !newNotesEnabled || isHistoricalNote ? <FormattedMessage id='timeline.system.note' values={{title: event.data.data.Title}}/> : <div>
          <div className='bold'>{event.data.data.title}</div>
          <div>{event.data.data.note}</div>
        </div>;
        break;
      case TimelineType.ContactAdded:
        description = <div>
          {event.data.data.NameFirst ? <div><FormattedMessage id='timeline.contact.name.first'
                                                              values={{name: event.data.data.NameFirst}}/></div> : null}
          {event.data.data.NameLast ? <div><FormattedMessage id='timeline.contact.name.last'
                                                             values={{name: event.data.data.NameLast}}/></div> : null}
          {event.data.data.Email ? <div><FormattedMessage id='timeline.contact.email'
                                                          values={{email: event.data.data.Email}}/></div> : null}
          {event.data.data.HomePhone ? <div><FormattedMessage id='timeline.contact.phone.home'
                                                              values={{phone: event.data.data.HomePhone}}/>
          </div> : null}
          {event.data.data.MobilePhone ? <div><FormattedMessage id='timeline.contact.phone.mobile'
                                                                values={{phone: event.data.data.MobilePhone}}/>
          </div> : null}
          {event.data.data.Organization ? <div><FormattedMessage id='timeline.contact.organization'
                                                                 values={{organization: event.data.data.Organization}}/>
          </div> : null}
        </div>;
        break;
      case TimelineType.ContactDeleted:
        description = <div>
          {event.data.data.NameFirst ? <div><FormattedMessage id='timeline.contact.name.first'
                                                              values={{name: event.data.data.NameFirst}}/></div> : null}
          {event.data.data.NameLast ? <div><FormattedMessage id='timeline.contact.name.last'
                                                             values={{name: event.data.data.NameLast}}/></div> : null}
        </div>;
        break;
      case TimelineType.ContactUpdated:
        description =
          <div>
            <div>{event.data.data.NameFirst && event.data.data.NameLast ? event.data.data.NameFirst + ' ' + event.data.data.NameLast : null}</div>
            {formatAccountContactUpdated(event.data.changes, formatMessage)}
          </div>;
        break;
      case TimelineType.SystemImported:
      case TimelineType.SystemAdded:
      case TimelineType.SystemDeleted:
        description = <div>
          {event.data.data.Volume ?
            <div> <FormattedMessage
            id='timeline.system.volume'
            values={{volume: event.data.data.Volume}}/>
            </div> : null}
          {event.data.data.Finish ?
            <div>  <FormattedMessage
            id='timeline.system.finish'
            values={{finish: formatMessage({id: getLabelID(systemOptionsTags[event.data.data.TypeID].finish[event.data.data.Finish])})}}/>
            </div> : null}
          {event.data.data.Location ?
            <div> <FormattedMessage
            id='timeline.system.location'
            values={{location: formatMessage({id: getLabelID(systemOptionsTags[event.data.data.TypeID].location[event.data.data.Location])})}}/>
            </div> : null}
          {event.data.data.GroundLevel ?
            <div> <FormattedMessage
            id='timeline.system.groundLevel'
            values={{groundLevel: formatMessage({id: getLabelID(systemOptionsTags[event.data.data.TypeID].groundLevel[event.data.data.GroundLevel])})}}/>
            </div> : null}
          {event.data.data.property ?
            <div> <FormattedMessage
            id='timeline.system.property'
            values={{property: formatMessage({id: getLabelID(systemOptionsTags[event.data.data.TypeID].property[event.data.data.property])})}}/>
            </div> : null}
          {event.data.data.FilterMedia ?
            <div> <FormattedMessage
            id='timeline.system.filterMedia'
            values={{filterMedia: formatMessage({id: getLabelID(systemOptionsTags[event.data.data.TypeID].filterMedia[event.data.data.FilterMedia || ''])})}}/>
            </div> : null}
        </div>;
        break;
      case TimelineType.SystemAnalysisUpdated:
        description = <div>{event.userEmail ? <FormattedMessage id='timeline.analysis.editted'
                                                                values={{email: event.userEmail}}/> : null}</div>;
        break;
      case TimelineType.SourceWaterUpdated:
        description = <div>
          <div className={getHighlighted(event.data.changes, 'TDS')}>{event.data.data.TDS ?
            <FormattedMessage id='timeline.analysis.tds'
                              values={{tds: event.data.data.TDS}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'pH')}>{event.data.data.pH ?
            <FormattedMessage id='timeline.analysis.pH'
                              values={{pH: event.data.data.pH}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'Copper')}>{event.data.data.Copper ?
            <FormattedMessage id='timeline.analysis.copper'
                              values={{copper: event.data.data.Copper}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'TotalChlorine')}>{event.data.data.TotalChlorine ?
            <FormattedMessage id='timeline.analysis.chlorine.total'
                              values={{totalChlorine: event.data.data.TotalChlorine}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'FreeChlorine')}>{event.data.data.Alkalinity ?
            <FormattedMessage id='timeline.analysis.chlorine.free'
                              values={{freeChlorine: event.data.data.FreeChlorine}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'Alkalinity')}>{event.data.data.Alkalinity ?
            <FormattedMessage id='timeline.analysis.alkalinity'
                              values={{alkalinity: event.data.data.Alkalinity}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'Hardness')}>{event.data.data.Hardness ?
            <FormattedMessage id='timeline.analysis.hardness'
                              values={{hardness: event.data.data.Hardness}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'Iron')}>{event.data.data.Iron ?
            <FormattedMessage id='timeline.analysis.iron'
                              values={{iron: event.data.data.Iron}}/> : null}</div>
          <div className={getHighlighted(event.data.changes, 'Manganese')}>{event.data.data.Manganese ?
            <FormattedMessage id='timeline.analysis.manganese'
                              values={{manganese: event.data.data.Manganese}}/> : null}</div>
        </div>;
        break;
      case TimelineType.SystemUpdated:
        description = <div>{formatSystemObject(event.data.data.TypeID, event.data.changes, formatMessage, branch, event.data.data.ChemicalBaseTypeID)}</div>;
        break;
      case TimelineType.SystemMerge:
        description = <div>
          <div><FormattedMessage id='timeline.system.merged'
                                 values={{old: event.data.data.ID, new: event.PoolID}}/></div>
          <div><FormattedMessage id='timeline.system.merged.details'/></div>
          {event.data.data.Volume ? <div><FormattedMessage id='timeline.system.volume'
                                                           values={{volume: event.data.data.Volume}}/></div> : null}
          {event.data.data.Finish ?
            <div><FormattedMessage
              id='timeline.system.finish'
              values={{finish: formatMessage({id: systemOptionsTags[event.data.data.TypeID].finish[event.data.data.Finish]})}}/>
            </div> : null}
          {event.data.data.Location ?
            <div><FormattedMessage
              id='timeline.system.location'
              values={{location: formatMessage({id: systemOptionsTags[event.data.data.TypeID].location[event.data.data.Location]})}}/>
            </div> : null}
        </div>;
        break;
      case TimelineType.AccountMergeInto:
        description = <div>
          <div><FormattedMessage id='timeline.system.merged.below'/></div>
          {accountMergeInfo(event.data.data)}
        </div>;
        break;
      case TimelineType.ReportHistoryEmail:
      case TimelineType.SeasonUsageEmail:
        description = <div>
          <FormattedMessage id='timeline.emails.sent'
                            values={{emails: getArrayDataEmails(event.data ? event.data.contactsEmailed : '', currentContacts)}}/>
        </div>;
        break;
      case TimelineType.AnalysisEmail:
        description = <div>
          <div><FormattedMessage id='timeline.email.analysis.id'
                                 values={{id: event.data.data.ID}}/></div>
          <div><FormattedMessage id='timeline.email.analysis.date'
                                 values={{date: moment(event.data.data.Created).format('ll')}}/></div>
          <div><FormattedMessage id='timeline.emails.sent'
                                 values={{
                                   emails: <React.Fragment>{getArrayDataEmails(event.data ? event.data.contactsEmailed :
                                     null, currentContacts)}</React.Fragment>,
                                 }}/>
          </div>
        </div>;
        break;
      case TimelineType.FollowUpEmailed:
        description = <div>
          <div><FormattedMessage id='timeline.follow-up.date'
                                 values={{date: moment(event.data.data.FollowUpDate).format('ll')}}/></div>
          <div><FormattedMessage id='timeline.follow-up.type'
                                 values={{type: followUpTypeIDs(formatMessage)[event.data.data.FollowUpTypeID]}}/></div>
          <div><FormattedMessage id='timeline.emails.sent'
                                 values={{emails: getArrayDataEmails(event.data ? event.data.contactsEmailed : null, currentContacts)}}/>
          </div>
        </div>;
        break;
      case TimelineType.PastDueCustomerEmailed:
      case TimelineType.WinterProductReminderEmailed:
      case TimelineType.InactiveCustomerEmailed:
      case TimelineType.WinterClosingEmailed:
      case TimelineType.DirectEmail:
      case TimelineType.WelcomeEmail:
        description = <div>
          <FormattedMessage
            id='timeline.emails.sent'
            values={{emails: getArrayDataEmails(event.data ? event.data.contactsEmailed : null, currentContacts)}}/>
        </div>;
        break;
      case TimelineType.EncyclopediaEmail:
        description = <div>
          <div><FormattedMessage id='timeline.article.title'
                                 values={{title: event.data.data}}/></div>
          <div>
            <FormattedMessage
              id='timeline.emails.sent'
              values={{emails: getArrayDataEmails(event.data ? event.data.contactsEmailed : null, currentContacts)}}/>
          </div>
        </div>;
        break;
      case TimelineType.CustomerAddedAlexProScript:
      case TimelineType.CustomerAddedAlexDesktopScript:
      case TimelineType.CustomerImportedScript:
        description = <div>
          <FormattedMessage id='timeline.script.created'
          values={{object: formatMessage({id: 'general.customer'}), alexlocation: formatMessage({id: 'timeline.location.alexpro'})}}/>
        </div>;
        break;
      case TimelineType.CustomerNoteScript:
        description = <div>
          <FormattedMessage id='timeline.script.note.added'
          values={{object: formatMessage({id: 'general.customer'}), note: event.data.NoteTitle}}/>
        </div>;
        break;
      case TimelineType.CustomerContactScript:
        description = <div>
          <FormattedMessage id='timeline.script.contact.added'
          values={{fullname: event.data.FullName}}/>
        </div>;
        break;
      case TimelineType.SystemAddedAlexProScript:
      case TimelineType.SystemAddedAlexDesktopScript:
        description = <div>
          <div><FormattedMessage id='timeline.script.added'
                         values={{object: systemTypeFullNamesFormat(formatMessage)[event.data.SystemType],
                           alexlocation: event.type === TimelineType.SystemAddedAlexProScript ?
                           formatMessage({id: 'timeline.location.alexpro'}) :
                           formatMessage({id: 'timeline.location.alexdesktop'})}}/>
          </div>
          <div><FormattedMessage id='timeline.system.type'
                         values={{type: systemTypeNamesFormat(formatMessage)[event.data.SystemType]}}/></div>
          <div><FormattedMessage id='timeline.system.volume'
                         values={{volume: event.data.Volume}}/></div>
        </div>;
        break;
      case TimelineType.SystemImportedScript:
        description = <div>
          <div><FormattedMessage id='timeline.script.imported'
                     values={{object: systemTypeFullNamesFormat(formatMessage)[event.data.SystemType],
                       alexlocation: event.type === TimelineType.SystemAddedAlexProScript ?
                       formatMessage({id: 'timeline.location.alexpro'}) :
                       formatMessage({id: 'timeline.location.alexdesktop'})}}/>
          </div>
          <div><FormattedMessage id='timeline.system.type'
                     values={{type: systemTypeNamesFormat(formatMessage)[event.data.SystemType]}}/></div>
          <div><FormattedMessage id='timeline.system.volume'
                     values={{volume: event.data.Volume}}/></div>
        </div>;
        break;
      case TimelineType.SystemNoteScript:
        description = <div>
          <FormattedMessage id='timeline.script.note.added'
          values={{object: formatMessage({id: 'general.system'}), note: event.data.NoteTitle}}/>
        </div>;
        break;
      case TimelineType.FollowUpAddedAlexProScript:
        description = <div>
          <FormattedMessage id='timeline.script.scheduled'
            values={{object: formatMessage({id: 'general.followup'}), alexlocation: formatMessage({id: 'timeline.location.alexpro'})}}/>
        </div>;
        break;
      case TimelineType.FollowUpAddedAlexDesktopScript:
        description = <div>
          <FormattedMessage id='timeline.script.scheduled'
        values={{object: formatMessage({id: 'general.followup'}), alexlocation: formatMessage({id: 'timeline.location.alexpro'})}}/>
        </div>;
        break;
      case TimelineType.FollowUpImportedScript:
        description = <div>
          <FormattedMessage id='timeline.script.imported'
         values={{object: formatMessage({id: 'general.followup'}), alexlocation: formatMessage({id: 'timeline.location.alexdesktop'})}}/>
        </div>;
        break;
      case TimelineType.AnalysisAddedAlexProScript:
        description = <div>
          <FormattedMessage id='timeline.script.conducted'
           values={{object: formatMessage({id: 'general.followup'}), alexlocation: formatMessage({id: 'timeline.location.alexpro'})}}/>
        </div>;
        break;
      case TimelineType.AnalysisAddedAlexDesktopScript:
        description = <div>
          <FormattedMessage id='timeline.script.conducted'
           values={{object: formatMessage({id: 'general.followup'}), alexlocation: formatMessage({id: 'timeline.location.alexdesktop'})}}/>
        </div>;
        break;
      case TimelineType.AnalysisImportedScript:
        description = <div>
          <FormattedMessage id='timeline.script.imported'
           values={{object: formatMessage({id: 'general.followup'}), alexlocation: formatMessage({id: 'timeline.location.alexpro'})}}/>
        </div>;
        break;
      case TimelineType.BecameTruBlu:
        description = <div>
          <div><FormattedMessage id='timeline.trublue.added'
                                 values={{date: moment(event.data.data.TruBlue).format('ll h:mm a')}}/></div>
        </div>;
        break;
      case TimelineType.RemovedTruBlu:
        description = <div>
          <div><FormattedMessage id='timeline.trublue.removed'
                                 values={{date: moment(event.data.data.TruBlueExpiry).format('ll h:mm a')}}/></div>
        </div>;
        break;
      case TimelineType.AccountActivated:
        description = <div>
          <div><FormattedMessage id='timeline.trublue.reactivate'
                                 values={{date: moment(event.data.data.Modified).format('ll h:mm a')}}/></div>
        </div>;
        break;
      case TimelineType.AccountDeactivated:
        description = <div>
          <div><FormattedMessage id='timeline.trublue.deactivate'
                                 values={{date: moment(event.data.data.Modified).format('ll h:mm a')}}/></div>
        </div>;
        break;
      case TimelineType.SourceWaterTested:
        description = <div><FormattedMessage id='timeline.analysis.source'/></div>;
        break;
      case TimelineType.SourceConcern:
        description = <div><i className='bl bl-SourceConcern'/></div>;
        break;
      case TimelineType.SeasonUsageUpdated:
        description = <div>
          <div><FormattedMessage id='timeline.system.season.modified'
                                 values={{date: moment(event.data.data.Modified).format('ll h:mm a')}}/></div>
          <div><FormattedMessage id='timeline.system.season.start'
                                 values={{date: moment(event.data.data.SeasonStart).format('ll')}}/></div>
          <div><FormattedMessage id='timeline.system.season.end'
                                 values={{date: moment(event.data.data.SeasonEnd).format('ll')}}/></div>
        </div>;
        break;
      case TimelineType.AccountEmailPref:
        description = <div className='emil-pref-expanded'>
          <div>{event.data.data.CustomerEmail ? 'Email Preferences for ' + event.data.data.CustomerEmail : null}</div>
          <div className='preference-data'>
            <i className={event.data.data.LatestFromBioguard ? 'bl bl-checkmark' : 'bl bl-x'}/>
            <div><FormattedMessage id='manifold.emailprefs.latestbioguard.title'/></div>
          </div>
          <div className='preference-data'>
            <i className={event.data.data.AccountUpdate ? 'bl bl-checkmark' : 'bl bl-x'}/>
            <div><FormattedMessage id='manifold.emailprefs.alexpro.title'/></div>
          </div>
          <div className='preference-data'>
            <i className={event.data.data.NewsFromExpert ? 'bl bl-checkmark' : 'bl bl-x'}/>
            <div><FormattedMessage id='manifold.emailprefs.newsexpert.title'/></div>
          </div>
        </div>;
        break;
      case TimelineType.AccountStarred:
        description = <div className='big-star'><i className='bl bl-star-filled'/></div>;
        break;
      case TimelineType.Anniversary:
        description = <FormattedMessage id='customer.timeline.year.anniversary' values={{year: event.data.year}}/>;
        break;
      case TimelineType.Product:
        description =
          <div>
            <div className='description-bottom-spacing'>
              <strong>
                <FormattedMessage id='timeline.purchases.total'
                                  values={{total: event.data.data ? formatCurrency(event.data.data.AlexSubTotal) : null}}/>
              </strong>
            </div>
            {formatInvoice(event.data.data && event.data.data.Products ? event.data.data.Products : null)}
          </div>;
        break;
      case TimelineType.AnalysisNoteAdded:
      case TimelineType.AnalysisNoteUpdated:
        description = !newNotesEnabled || isHistoricalNote ? event.data.data.Title : <div>
          <div className='bold'>{event.data.data.title}</div>
          <div>{event.data.data.note}</div>
        </div>;
        break;
      default:
        description = '';
    }
  }
  return description;
}

